import ListItemWrapper from '../../ListItemWrapper';
import Column from '../../Column';
import { Link } from 'react-router-dom';
import { buildDashboardShow } from '../../../../../navigation/appRoutes';
import { useContext, useMemo } from 'react';
import { PortalsContext } from '../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import Typography from '../../../../../kingpin/atoms/Typography';
import { FieldUsageEntities } from './types';
import FieldUsageEntity from './FieldUsageEntity';
import styled from 'styled-components';
import cardTypeCheckers from '../../../../../types/cardTypeCheckers';

const List = styled.ul`
  margin: 0;
`;

const DashboardFieldUsage = ({
  dashboard,
  usage,
}: {
  dashboard: DashboardType;
  usage: FieldUsageEntities;
}) => {
  const { selectedPortal } = useContext(PortalsContext);
  const dashboardLink = buildDashboardShow({
    id: dashboard.id,
    portal: selectedPortal,
  });

  const cards = dashboard.canvas?.cards;

  const {
    chartDefIdsOnDashboard,
    gadgetIdsOnDashboard,
    metricListIdsOnDashboard,
  } = useMemo(() => {
    const chartDefIds = new Set<string>();
    const gadgetIds = new Set<string>();
    const metricListIds = new Set<string>();

    cards.forEach((c) => {
      if (!c.content) return;

      if (cardTypeCheckers.isChartDefinition(c)) {
        chartDefIds.add(c.content.chartDefinitionId);
      } else if (cardTypeCheckers.isDashboardGadget(c)) {
        gadgetIds.add(c.content.dashboardGadgetId);
      } else if (cardTypeCheckers.isMetricList(c)) {
        metricListIds.add(c.content.metricListId);
      }
    });

    return {
      chartDefIdsOnDashboard: chartDefIds,
      gadgetIdsOnDashboard: gadgetIds,
      metricListIdsOnDashboard: metricListIds,
    };
  }, [cards]);

  const relevant = useMemo(() => {
    const metricLists = usage.metricLists.filter((ml) => {
      return (
        ml.dashboardId === dashboard.id || metricListIdsOnDashboard.has(ml.id)
      );
    });

    const dashboardGadgetFilters = (usage.dashboardGadgetFilters || []).filter(
      (g) => {
        return (
          gadgetIdsOnDashboard.has(g.id) ||
          chartDefIdsOnDashboard.has(g.chartDef?.id)
        );
      },
    );

    const gadgetDimensions = (usage.gadgetDimensions || []).filter((d) => {
      return chartDefIdsOnDashboard.has(d.id);
    });

    const simpleGrids = (usage.simpleGrids || []).filter((d) => {
      return chartDefIdsOnDashboard.has(d.id);
    });

    const simpleGridSorts = (usage.simpleGridSorts || []).filter((d) => {
      return chartDefIdsOnDashboard.has(d.id);
    });

    return {
      metricLists,
      dashboardGadgetFilters,
      gadgetDimensions,
      simpleGrids,
      simpleGridSorts,
    };
  }, [
    usage,
    dashboard.id,
    chartDefIdsOnDashboard,
    gadgetIdsOnDashboard,
    metricListIdsOnDashboard,
  ]);

  const hasAny =
    relevant.metricLists.length > 0 ||
    relevant.dashboardGadgetFilters.length > 0 ||
    relevant.gadgetDimensions.length > 0 ||
    relevant.simpleGrids.length > 0 ||
    relevant.simpleGridSorts.length > 0;

  return (
    <ListItemWrapper style={{ height: 'auto', minHeight: 56, padding: 12 }}>
      <div>
        <Column style={{ minHeight: 0 }}>
          <Typography.Body type={'Body 14 Bold'}>
            <Link target={'_blank'} to={dashboardLink}>
              {dashboard.name}
            </Link>
          </Typography.Body>
        </Column>
        {hasAny && (
          <div style={{ marginTop: 8 }}>
            {relevant.metricLists.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Typography.Body type={'Body 13 Bold'}>
                  Metric Lists ({relevant.metricLists.length})
                </Typography.Body>
                <List>
                  {relevant.metricLists.map((ml) => (
                    <li key={ml.id} style={{ fontSize: 12 }}>
                      <Link
                        to={`${dashboardLink}?gadgetId=${ml.id}`}
                        target={'_blank'}
                      >
                        {ml.name}
                      </Link>
                    </li>
                  ))}
                </List>
              </div>
            )}
            {relevant.dashboardGadgetFilters.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Typography.Body type={'Body 13 Bold'}>
                  Gadget Filters ({relevant.dashboardGadgetFilters.length})
                </Typography.Body>
                <List>
                  {relevant.dashboardGadgetFilters.map((g) => (
                    <FieldUsageEntity
                      link={`${dashboardLink}?gadgetId=${g.id}`}
                      key={g.id}
                      name={g.chartDef.name}
                    />
                  ))}
                </List>
              </div>
            )}
            {relevant.gadgetDimensions.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Typography.Body type={'Body 13 Bold'}>
                  Gadget Dimensions ({relevant.gadgetDimensions.length})
                </Typography.Body>
                <List>
                  {relevant.gadgetDimensions.map((d) => (
                    <FieldUsageEntity
                      link={`${dashboardLink}?gadgetId=${d.id}`}
                      key={d.id}
                      name={d.name}
                    />
                  ))}
                </List>
              </div>
            )}
            {relevant.simpleGrids.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Typography.Body type={'Body 13 Bold'}>
                  Simple Grids ({relevant.simpleGrids.length})
                </Typography.Body>
                <List>
                  {relevant.simpleGrids.map((d) => (
                    <li key={d.id} style={{ fontSize: 12 }}>
                      {d.name}
                    </li>
                  ))}
                </List>
              </div>
            )}
            {relevant.simpleGridSorts.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <Typography.Body type={'Body 13 Bold'}>
                  Simple Grid Sorts ({relevant.simpleGridSorts.length})
                </Typography.Body>
                <List>
                  {relevant.simpleGridSorts.map((d) => (
                    <li key={d.id} style={{ fontSize: 12 }}>
                      {d.name}
                    </li>
                  ))}
                </List>
              </div>
            )}
          </div>
        )}
      </div>
    </ListItemWrapper>
  );
};

export default DashboardFieldUsage;
